import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Lightbulb, ArrowRight, Check, X, Loader2, Sparkles } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function OpportunitiesPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("New");
  const load = async () => {
    setLoading(true);
    try {
      const t = localStorage.getItem("token");
      const r = await axios.get(`${API}/opportunities?status=${tab}`,
        { headers: { Authorization: `Bearer ${t}` } });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const setStatus = async (id, status) => {
    try {
      const t = localStorage.getItem("token");
      await axios.patch(`${API}/opportunities/${id}`, { status },
        { headers: { Authorization: `Bearer ${t}` } });
      toast.success(status === "Dismissed" ? "Dismissed" : "Reviewed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden" data-testid="opportunities-page">
      <div className="max-w-3xl mx-auto p-6 md:p-10 space-y-6">
        <div>
          <div className="flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-amber-300/80"/>
            <h1 className="text-3xl font-display font-semibold tracking-tight">Opportunities</h1>
          </div>
          <p className="text-sm text-white/50 mt-1 max-w-2xl">
            Proactive relationship hypotheses HQ surfaces from your canonical graph. Every card carries facts, a connection path, and a Possible fit — never presented as decided facts.
          </p>
        </div>
        <div className="inline-flex rounded-full border border-white/10 p-0.5 bg-white/[0.03] text-xs">
          {["New", "Reviewed", "Dismissed"].map(s => (
            <button key={s} onClick={() => setTab(s)}
              className={`px-3 py-1 rounded-full transition ${tab === s ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
              data-testid={`opps-tab-${s.toLowerCase()}`}>{s}</button>
          ))}
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12 text-white/40">
            <Loader2 className="w-5 h-5 animate-spin mr-2"/> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/10 bg-white/[0.01] p-10 text-center text-white/50 text-sm"
               data-testid="opps-empty">
            No {tab.toLowerCase()} opportunities yet.
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map(o => <OppCard key={o.id} o={o} setStatus={setStatus}/>)}
          </div>
        )}
      </div>
    </div>
  );
}

function OppCard({ o, setStatus }) {
  return (
    <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02]"
         data-testid={`opp-card-${o.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-white font-medium">{o.title}</div>
          <div className="text-xs text-white/60 mt-1">{o.summary}</div>
        </div>
        <div className="flex items-center gap-2">
          {o.materially_new && <Badge variant="outline" className="border-amber-400/40 bg-amber-500/10 text-amber-300">Materially new</Badge>}
          <span className="text-[11px] text-white/50">{Math.round((o.confidence || 0) * 100)}%</span>
        </div>
      </div>
      {(o.facts || []).length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-white/40">Facts</div>
          <ul className="text-xs text-white/70 mt-1 space-y-0.5">
            {o.facts.map((f, i) => <li key={i}>· {f}</li>)}
          </ul>
        </div>
      )}
      {(o.connection_path || []).length > 0 && (
        <div className="mt-3" data-testid={`opp-path-${o.id}`}>
          <div className="text-[10px] uppercase tracking-wider text-white/40">Connection</div>
          <div className="text-xs text-white/75 mt-1 flex flex-wrap items-center gap-1">
            {o.connection_path.map((step, i) => (
              <React.Fragment key={i}>
                {i > 0 && <ArrowRight className="w-3 h-3 text-white/30"/>}
                <span className={step.hypothesis ? "text-amber-300 italic" : ""}>
                  {step.label}{step.status ? ` · ${step.status}` : ""}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}
      {o.possible_fit && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-amber-300/80">Possible fit</div>
          <div className="text-xs text-white/70 italic mt-1">{o.possible_fit}</div>
        </div>
      )}
      {o.status !== "Dismissed" && o.status !== "Reviewed" && (
        <div className="mt-3 flex items-center justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={() => setStatus(o.id, "Dismissed")}
            className="text-white/60 hover:text-white hover:bg-white/10"
            data-testid={`opp-dismiss-${o.id}`}>
            <X className="w-3.5 h-3.5 mr-1"/> Dismiss
          </Button>
          <Button size="sm" onClick={() => setStatus(o.id, "Reviewed")}
            className="rounded-full bg-white text-black hover:bg-white/90"
            data-testid={`opp-review-${o.id}`}>
            <Check className="w-3.5 h-3.5 mr-1"/> Mark reviewed
          </Button>
        </div>
      )}
    </div>
  );
}
